/**
 * Retention Expired Banner
 *
 * Bannière d'information affichée quand une ressource dont le détail est
 * soumis à une politique de rétention (purge périodique en base) est
 * consultée au-delà de la fenêtre de rétention. Le résumé reste toujours
 * disponible : seul le détail ligne-à-ligne a été purgé.
 *
 * Design volontairement "info" (bleu, pas rouge) : ce n'est pas une erreur,
 * c'est un comportement attendu du système — voir sprint-optim-postgres-cost
 * US-09 (dégradation gracieuse après la purge US-07/US-08).
 */

import { Info } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface RetentionExpiredBannerProps {
  /** Nombre de mois de rétention (ex: 3) */
  retentionMonths: number;
  /**
   * Complément inséré après "Détail" dans le titre, avec son article inclus
   * (ex: "des consommations théoriques", "du ticket"). Permet d'adapter la
   * phrase au contexte (onglet Produits Bruts vs drawer ticket) sans
   * casser la grammaire.
   * @default 'des consommations théoriques'
   */
  resource?: string;
  /** Classes additionnelles */
  className?: string;
}

export function RetentionExpiredBanner({
  retentionMonths,
  resource = 'des consommations théoriques',
  className,
}: RetentionExpiredBannerProps) {
  return (
    <div
      role="status"
      className={cn('rounded-md bg-blue-50 border border-blue-200 p-4', className)}
    >
      <div className="flex gap-3">
        <Info className="text-blue-600 w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-medium">Détail {resource} indisponible</p>
          <p className="mt-1">
            Pour optimiser les performances, le détail par ingrédient est
            conservé pendant <strong>{retentionMonths} mois</strong>. Au-delà
            de cette période, seul le résumé (montants, marge, articles
            vendus) reste disponible.
          </p>
          <p className="mt-2 text-xs text-blue-700">
            Besoin d'un audit sur cette période ? Contactez le support, qui
            peut restaurer le détail depuis les archives.
          </p>
        </div>
      </div>
    </div>
  );
}
